import React from 'react';
import type { FC } from 'react';
import { useTranslation } from 'next-i18next';
import Image from 'next/legacy/image';
import UniversalLink from 'src/common/components/UniversalLink';
import stickers from 'src/common/components/Sticker/stickerMap';
import { NEXT_STATIC_IMAGES, PATHS } from 'src/common/constants';
import Typography from 'src/common/components/Typography';
import Icon from 'src/common/components/IconDS22';
import getClassNames from 'src/common/utils/getClassNames';
import classNames from 'classnames';
import styles from './BoatTypeCard.module.scss';

export type BoatTypeCardProps = {
  /**
   * Designed for search-go formatted strings of `category-slug` or `category-slug/location-slug`
   * The category-slug is used to lookup stickers
   */
  urlSegment: string;
  /**
   * Where the cards links (UniversalLink). Defaults to sticker category pages.
   * DLP overrides to category/location pages
   */
  href?: string;
  /** Text next to button. Defaults to "View more. Useful to show price. */
  ctaText?: string;
  hasCta?: boolean;
  hasDescription?: boolean;
};

const BoatTypeCard: FC<BoatTypeCardProps> = ({
  urlSegment,
  href,
  ctaText,
  hasCta = true,
  hasDescription = true,
}) => {
  const [t] = useTranslation('boat-type-cards');

  const categorySlug = urlSegment.split('/')[0]; // sticker name and translations can be matched by category slug

  const sticker = stickers.find(({ slug }) => slug === categorySlug);

  const categoryHref = `${PATHS.SEARCH}${urlSegment}`;

  return (
    <UniversalLink
      href={href ?? categoryHref}
      className={getClassNames(styles, 'root', classNames({ hasDescription }))}
    >
      <div className={styles.image}>
        <div
          className={styles.sticker}
          style={{
            aspectRatio: `${sticker?.width}/${sticker?.height}`,
          }}
        >
          <Image
            alt={sticker?.name}
            src={`${NEXT_STATIC_IMAGES}stickers/${sticker?.name}.png`}
            width={sticker?.width}
            height={sticker?.height}
          />
        </div>
      </div>
      <Typography
        size="body-xl"
        type="p"
        weight="semibold"
        className={styles.title}
      >
        {t(`sticker--${categorySlug}`, { context: 'title' })}
      </Typography>
      {hasDescription && (
        <Typography
          size="body-l"
          type="p"
          className={styles.description}
        >
          {t(`sticker--${categorySlug}`, { context: 'description' })}
        </Typography>
      )}
      {hasCta && (
        <div className={styles.cta}>
          <Typography
            size="body-l"
            weight="semibold"
            type="p"
          >
            {ctaText ?? t('boat-type-cta')}
          </Typography>
          <div className={styles.arrow}>
            <Icon
              id="arrow-right"
            />
          </div>
        </div>
      )}
    </UniversalLink>
  );
};

export default BoatTypeCard;
